/**
 * What the five opposition-research stages are CALLED on screen.
 *
 * ## Two problems with the stored codes, and only one of them is readability
 *
 * The codes are `RECON`, `WEAPONIZE`, `DEPLOY`, `SUSTAIN`, `NEUTRALIZE` — a literal military
 * kill chain. They reach the screen through a helper that only prettifies a code, so a
 * candidate's board showed chips reading "Weaponize" and "Neutralize" against a named real
 * person.
 *
 * The first problem is that an aspirant cannot tell you what "weaponize" means here. The
 * second is worse and is the reason this file exists rather than a few relabelled strings: a
 * screenshot of a page headed KILL CHAIN, with a stage called WEAPONIZE and another called
 * NEUTRALIZE, pointed at an opponent by name, writes its own headline — and it would be a
 * fair one to draw from those words, even though the feature underneath is ordinary,
 * legally-gated opposition research with an evidence floor and a clearance gate.
 *
 * So the labels describe what the stage actually requires, in the words a campaign uses.
 * Every name below is taken from the gate the service already enforces, not invented:
 *
 *   RECON       the file holds at least one item                    -> Research
 *   WEAPONIZE   two independent items above the floor, or one
 *               primary public record                              -> Build the case
 *   DEPLOY      every play legally cleared, named accountable owner -> Go public
 *   SUSTAIN     nothing linked disputed or debunked since going out -> Hold the line
 *   NEUTRALIZE  an outcome and an outcome note recorded             -> Closed out
 *
 * ## The codes are NOT renamed
 *
 * They are persisted on `opposition_operation`, they are in the enum snapshot, and they are
 * what the transition rows record. Changing them is a migration plus a snapshot regeneration
 * plus a rewrite of every stage gate — a far riskier change than it looks, and it buys
 * nothing a label cannot. The wire stays military; the room does not.
 */

/**
 * Stage order, as plain strings rather than a literal tuple: callers look positions up with
 * a code that arrives from the API as a `string`, and a readonly tuple makes every one of
 * those an assignability error for no benefit — the order is the thing that matters here,
 * not the narrowed type.
 */
export const STAGE_ORDER: string[] =
  ['RECON', 'WEAPONIZE', 'DEPLOY', 'SUSTAIN', 'NEUTRALIZE']

const STAGE_LABEL: Record<string, string> = {
  RECON: 'Research',
  WEAPONIZE: 'Build the case',
  DEPLOY: 'Go public',
  SUSTAIN: 'Hold the line',
  NEUTRALIZE: 'Closed out',
}

/** One line on what the stage is FOR, for the column head and the empty state. */
const STAGE_PURPOSE: Record<string, string> = {
  RECON: 'Finding out what is already on the public record about them',
  WEAPONIZE: 'Turning what we found into something a spokesperson can actually say',
  DEPLOY: 'Cleared, owned by a named person, and out in the campaign',
  SUSTAIN: 'Out there and holding up — nothing has been disputed since',
  NEUTRALIZE: 'Finished, with the outcome written down',
}

export function stageLabel(code: string | null | undefined): string {
  if (!code) return '—'
  return STAGE_LABEL[code] ?? code.replace(/_/g, ' ').toLowerCase()
}

export function stagePurpose(code: string | null | undefined): string {
  return (code && STAGE_PURPOSE[code]) || ''
}

/** What the next move needs, in plain terms. Mirrors the gate the service enforces. */
export const STAGE_GATE: Record<string, string> = {
  RECON: 'Needs: at least one item in the file on this person',
  WEAPONIZE: 'Needs: two items from sources that do not depend on each other, or one '
    + 'official record — a court file, gazette notice, asset declaration or company filing',
  DEPLOY: 'Needs: legal clearance on every line, and one named person accountable for it',
  SUSTAIN: 'Needs: nothing we published has been disputed or disproved since',
  NEUTRALIZE: 'Needs: the outcome recorded, and a note on what happened',
}
